import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MapPin, Navigation, Loader2, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useStatusContext } from '../App';
import { useToast, apiErrorMessage } from './Toast';
import { geocodeSearch, updateSite } from '../api/client';

interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
}

const LOCATE_ATTEMPTS = 3;
const LOCATE_GIVE_UP_MS = 30000;

// Routes where the prompt would get in the way (sign-in / registration funnel).
const HIDDEN_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];

/**
 * Prompts a signed-in user to set a location for any of their sites that has
 * no latitude/longitude (needed for the weather-based solar forecast).
 * Dismissal only lasts until the next page load / sign-in, so it keeps
 * prompting until the location is provided.
 */
export function LocationPromptBanner() {
  const { user, isAdmin } = useAuth();
  const { sitesStatus, refresh } = useStatusContext();
  const location = useLocation();
  const toast = useToast();

  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addressQuery, setAddressQuery] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(searchTimer.current), []);

  // Only the viewer's own sites — admins get every site in /status/sites.
  // Older API builds don't send user_id; there a non-admin's list is all theirs.
  const missing = (sitesStatus?.sites || []).filter(
    (site) =>
      (site.latitude == null || site.longitude == null) &&
      (site.user_id ? site.user_id === user?.id : !isAdmin)
  );
  const site = missing[0];

  if (!user || !site || dismissed || user.needs_subscription) return null;
  if (HIDDEN_PREFIXES.some((prefix) => location.pathname.startsWith(prefix))) return null;

  const saveLocation = async (latitude: number, longitude: number) => {
    setSaving(true);
    setError(null);
    try {
      await updateSite(site.id, {
        latitude: Number(latitude.toFixed(6)),
        longitude: Number(longitude.toFixed(6)),
      });
      toast.success(`Location saved for ${site.name}`);
      setExpanded(false);
      setAddressQuery('');
      setSuggestions([]);
      refresh();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save location'));
    } finally {
      setSaving(false);
    }
  };

  // Suburb-level accuracy is plenty for a solar forecast, so don't ask for a
  // slow high-accuracy fix. Failed lookups are retried quietly. The lookup that
  // raises the browser's permission prompt can stall after the user clicks
  // Allow (it only came back after a page refresh), so as soon as permission
  // flips to granted a fresh lookup is raced against it — first fix wins.
  const locateMe = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser. Search for your address instead.');
      return;
    }
    setGettingLocation(true);
    setError(null);

    let done = false;
    let failures = 0;
    const finish = (then: () => void) => {
      if (done) return;
      done = true;
      window.clearTimeout(watchdog);
      setGettingLocation(false);
      then();
    };
    const failed = () =>
      setError('Could not get your location. Try again, or search for your address instead.');
    const watchdog = window.setTimeout(() => finish(failed), LOCATE_GIVE_UP_MS);

    const request = () => {
      if (done) return;
      navigator.geolocation.getCurrentPosition(
        (position) =>
          finish(() => saveLocation(position.coords.latitude, position.coords.longitude)),
        (err) => {
          if (done) return;
          if (err.code === err.PERMISSION_DENIED) {
            finish(() =>
              setError('Location access denied. Allow it in your browser, or search for your address instead.')
            );
            return;
          }
          failures += 1;
          if (failures < LOCATE_ATTEMPTS) request();
          else finish(failed);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      );
    };
    request();

    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((permission) => {
        if (permission.state !== 'prompt') return;
        permission.onchange = () => {
          permission.onchange = null;
          if (permission.state === 'granted') request();
        };
      })
      .catch(() => {
        // Permissions API unavailable - the original request still stands
      });
  };

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }
    setSearching(true);
    try {
      // Via our API (Australia only), not straight to the geocoder.
      setSuggestions((await geocodeSearch(query)) as AddressSuggestion[]);
    } catch {
      // Silently fail - user can still use their current location
    }
    setSearching(false);
  };

  const handleAddressChange = (value: string) => {
    setAddressQuery(value);
    window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(() => searchAddress(value), 300);
  };

  const busy = saving || gettingLocation;

  return (
    <div className="mb-6 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-4">
      <div className="flex items-start">
        <MapPin className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
            Add your site location
          </h3>
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
            {missing.length > 1
              ? `${missing.length} of your sites have no location set, starting with ${site.name}. `
              : `${site.name} has no location set. `}
            We need it to forecast your solar production accurately.
          </p>

          {!expanded ? (
            <button
              onClick={() => setExpanded(true)}
              className="mt-3 px-3 py-1.5 text-sm font-medium bg-brand-orange text-white rounded-full hover:bg-brand-orange-dark transition-colors"
            >
              Set location
            </button>
          ) : (
            <div className="mt-3 space-y-3 max-w-xl">
              <div className="relative">
                <input
                  type="text"
                  value={addressQuery}
                  onChange={(e) => handleAddressChange(e.target.value)}
                  disabled={busy}
                  placeholder="Search for your address..."
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-input text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange disabled:opacity-50"
                />
                {searching && (
                  <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-gray-400" />
                )}
                {suggestions.length > 0 && (
                  <ul className="absolute z-20 mt-1 w-full card max-h-60 overflow-auto">
                    {suggestions.map((suggestion) => (
                      <li key={`${suggestion.lat},${suggestion.lon}`}>
                        <button
                          onClick={() => saveLocation(parseFloat(suggestion.lat), parseFloat(suggestion.lon))}
                          disabled={busy}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-brand-navy-dark disabled:opacity-50"
                        >
                          {suggestion.display_name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => locateMe()}
                disabled={busy}
                className="flex items-center gap-2 text-sm font-medium text-amber-800 dark:text-amber-300 hover:underline disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                {gettingLocation ? 'Getting your location...' : saving ? 'Saving...' : "I'm at the site — use my current location"}
              </button>
              {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
            </div>
          )}
        </div>
        <button
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="ml-3 text-amber-500 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
